import React from 'react';
import {
  Field,
  FluentProvider,
  Radio,
  RadioGroup,
  Textarea,
  webLightTheme,
} from '@fluentui/react-components';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Controller, UseFormMethods } from 'react-hook-form';
import CompanyPicker from '../../companyGroups/detail/CompanyGroupCompanyPicker';
import { validateCompany } from './companyValidation';
import DocumentsForm from '../../common/documents/DocumentsForm';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';
import { IEntityTerminationIntakeRequest } from '../types';

interface ITerminationIntakeRequestFormProps {
  form: UseFormMethods<IEntityTerminationIntakeRequest>;
  setUploadInProgress: (isUploading: boolean) => void;
}

interface IYesNoQuestion {
  name: 'hasEmployees' | 'hasRevenueContracts' | 'hasOtherOperatingExpenses';
  label: string;
}

const yesNoQuestions: IYesNoQuestion[] = [
  { name: 'hasEmployees', label: 'Are there employees?' },
  { name: 'hasRevenueContracts', label: 'Are there revenue contracts?' },
  { name: 'hasOtherOperatingExpenses', label: 'Are there other operating expenses?' },
];

// An empty string leaves both radios unselected until the user answers.
const toRadioValue = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return value ? yesOption.key : noOption.key;
};

const TerminationIntakeRequestForm = ({
  form,
  setUploadInProgress,
}: ITerminationIntakeRequestFormProps): JSX.Element => {
  const { control, errors, watch } = form;
  const selectedCompanyCode = watch('company')?.[0]?.companyCode;

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="company"
          control={control}
          defaultValue={[]}
          rules={{ validate: validateCompany }}
          render={({ onChange, value }) => (
            <CompanyPicker
              label="Company Code"
              selectedItems={value}
              setSelectedItems={onChange}
              itemLimit={1}
              required
              errorMessage={errors?.company?.message}
            />
          )}
        />
        <Controller
          name="businessReason"
          control={control}
          defaultValue=""
          rules={{
            required: 'Please enter a business reason.',
            validate: (value) => (value && value.trim() ? true : 'Please enter a business reason.'),
            maxLength: {
              value: 10000,
              message: 'Business Reason must be 10,000 characters or fewer.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label="Business Reason"
              required
              validationMessage={errors?.businessReason?.message}
            >
              <Textarea
                id="businessReason"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                resize="vertical"
                onChange={(_, data) => onChange(data.value)}
              />
            </Field>
          )}
        />
        {yesNoQuestions.map((question) => (
          <Controller
            key={question.name}
            name={question.name}
            control={control}
            defaultValue={null}
            render={({ onChange, value }) => (
              <Field label={question.label}>
                <RadioGroup
                  id={question.name}
                  value={toRadioValue(value)}
                  onChange={(_, data) => onChange(data.value === yesOption.key)}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            )}
          />
        ))}
        <Controller
          name="businessSponsorApprovalDocuments"
          control={control}
          defaultValue={[]}
          rules={{
            validate: (value: IDocument[]) =>
              (value && value.length > 0) ||
              'Please upload the business sponsor approval document.',
          }}
          render={({ onChange, value }) => (
            <div>
              <Field validationMessage={errors?.businessSponsorApprovalDocuments?.message}>
                <DocumentsForm
                  documentsLabelText="Business Sponsor Approval Documents"
                  documentsRequired
                  documents={value}
                  attachDocument={async (files: IDocument[]): Promise<void> => {
                    onChange([...(value ?? []), ...files]);
                    setUploadInProgress(false);
                  }}
                  deleteDocument={(index: number): void =>
                    onChange((value ?? []).filter((_: IDocument, i: number) => i !== index))
                  }
                  uploadInProgress={setUploadInProgress}
                  companyCode={selectedCompanyCode}
                  allowUpload={!!selectedCompanyCode}
                  uploadDisabledMessage="Select a company before uploading documents."
                />
              </Field>
            </div>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default TerminationIntakeRequestForm;
